import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useClient } from "#/hook/useClient";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import { withBoldValues } from "#/pages/ClientDetail/components/prv/prvText";
import type { PredictionSettings, ShipmentCard } from "#/types/prediction";
import { formatDate, formatWeekday } from "#/utils/dateTimeUtils";
import { anchorOf, formatMeasure, formatMinutes } from "../predictionUtils";

interface Props {
  card: ShipmentCard;
  settings: PredictionSettings;
  /** TMA admins only: also show the "how this was worked out" card. */
  showHow: boolean;
}

/**
 * The shipment explained in plain sentences, in the platform's accent card:
 * which deliveries and why they share the day, where it leaves from and why,
 * which trucks and drivers, and every warning the plan raised. Every sentence
 * is built from the same numbers the sections below show.
 */
const ShipmentOverview = ({ card, settings, showHow }: Props) => {
  const t = usePrvTranslation();
  const { i18n } = useTranslation();
  const { client } = useClient();
  const { shipment, predictions, plan } = card;
  const say = (key: string, values: Record<string, string | number> = {}) =>
    withBoldValues(t, `predictedDeliveries.shipment.overview.${key}`, values);

  const anchor = anchorOf(card);
  const date = formatDate(shipment.shipmentDate, client);
  const weekday = formatWeekday(shipment.shipmentDate, i18n.language);
  const limit = formatMinutes(shipment.groupingLimitMinutes);
  const lines: ReactNode[] = [];

  // 1. Which deliveries, and why together.
  if (predictions.length > 1) {
    lines.push(
      say("grouped", {
        count: predictions.length,
        date,
        weekday,
        names: predictions.map((prediction) => prediction.subClient.name).join(", "),
      })
    );
    lines.push(say("groupedWhy", { date, limit }));
    if (anchor) {
      lines.push(
        say("anchor", {
          subClient: anchor.subClient.name,
          gap: formatMeasure(Number(anchor.gapDays ?? 0)),
        })
      );
    }
  } else {
    lines.push(say("single", { subClient: anchor?.subClient.name ?? "--", date, weekday }));
    lines.push(
      shipment.roadTimesUsed
        ? say("singleWhy", { limit })
        : t("predictedDeliveries.shipment.overview.noRoadTimes")
    );
  }

  // 2. From where, and why.
  if (plan) {
    const requested = plan.categories.reduce((sum, category) => sum + category.requested, 0);
    const picked = plan.categories.reduce((sum, category) => sum + category.picked, 0);
    if (!plan.origin) {
      lines.push(t("predictedDeliveries.shipment.overview.noSite"));
    } else if (plan.originReason === "full_load") {
      lines.push(say("from", { site: plan.origin.name, km: plan.originToFirstStopKm ?? "--" }));
    } else {
      lines.push(
        say("fromShort", {
          site: plan.origin.name,
          km: plan.originToFirstStopKm ?? "--",
          picked,
          requested,
        })
      );
    }

    // 3. Trucks and drivers.
    if (plan.origin) {
      if (plan.trucks.length === 0) {
        lines.push(say("noTruck", { site: plan.origin.name }));
      } else if (plan.warnings.includes("missing_data")) {
        lines.push(t("predictedDeliveries.shipment.overview.missingData"));
      } else if (plan.trips.length === 1) {
        const [trip] = plan.trips;
        lines.push(
          say(trip.driver ? "oneTrip" : "oneTripNoDriver", {
            truck: trip.truck.name,
            units: trip.units,
            kg: formatMeasure(trip.kg),
            m3: formatMeasure(trip.m3),
            driver: trip.driver?.name ?? "--",
            depart: trip.departAt ?? "--",
            return: trip.returnAt ?? "--",
          })
        );
      } else if (plan.trips.length > 1) {
        lines.push(
          say("manyTrips", {
            trips: plan.trips.length,
            trucks: new Set(plan.trips.map((trip) => trip.truck.vehicleId)).size,
            days: plan.days,
          })
        );
      }
    }

    // 4. Warnings, each once.
    if (plan.warnings.includes("short_stock")) lines.push(say("shortStock", { picked, requested }));
    if (plan.warnings.includes("spills_over_days")) {
      const last = Math.max(...plan.trips.map((trip) => trip.dayOffset));
      lines.push(say("spills", { days: last }));
    }
    if (plan.warnings.includes("exceeds_truck")) {
      lines.push(t("predictedDeliveries.shipment.overview.exceedsTruck"));
    }
    if (plan.warnings.includes("exceeds_day")) {
      lines.push(t("predictedDeliveries.shipment.overview.exceedsDay"));
    }
    if (plan.warnings.includes("no_driver") && plan.trips.length > 1) {
      lines.push(t("predictedDeliveries.shipment.overview.noDriver"));
    }
    if (plan.warnings.includes("no_road_times")) {
      lines.push(t("predictedDeliveries.shipment.overview.noRoadTimesLive"));
    }
  } else {
    lines.push(t("predictedDeliveries.shipment.overview.nothingToLoad"));
  }

  // How this was worked out: the rule behind each line above, with this
  // shipment's numbers. TMA admins only (Margaux, 2026-09-29: "this is our
  // intelligence"); a client user never sees it.
  const how: ReactNode[] = [];
  if (showHow) {
    const sayHow = (key: string, values: Record<string, string | number> = {}) =>
      withBoldValues(t, `predictedDeliveries.shipment.how.${key}`, values);
    const anchorGap = formatMeasure(Number(anchor?.gapDays ?? 0));
    how.push(
      sayHow(predictions.length > 1 ? "range" : "rangeSingle", {
        gap: anchorGap,
        half: formatMeasure((0.1 * Number(anchor?.gapDays ?? 0)) / 2),
      })
    );
    how.push(sayHow("day"));
    how.push(sayHow("limit", { limit }));
    if (!shipment.roadTimesUsed) how.push(t("predictedDeliveries.shipment.how.noRoadTimes"));
    if (plan?.origin) {
      how.push(sayHow(plan.originReason === "full_load" ? "site" : "siteShort"));
      how.push(t("predictedDeliveries.shipment.how.items"));
      if (plan.trips.length > 0) {
        how.push(sayHow(plan.trips.length === 1 ? "oneTruck" : "manyTrucks"));
        how.push(sayHow("days", { offloading: settings.offloadingMinutes }));
      }
    }
  }

  const overview = (
    <section className="flex flex-col gap-2 rounded-lg border border-l-4 border-orange-200 border-l-orange-500 bg-orange-50/60 px-4 py-3">
      <span className="text-[11px] font-semibold tracking-wider text-orange-700 uppercase">
        {t("predictedDeliveries.shipment.overview.title")}
      </span>
      <ul className="flex list-disc flex-col gap-1.5 pl-4 text-sm text-gray-700">
        {lines.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
      <span className="text-xs text-gray-500">
        {t("predictedDeliveries.shipment.overview.settings", {
          limit,
          offloading: settings.offloadingMinutes,
        })}
      </span>
    </section>
  );
  if (!showHow) return overview;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {overview}
      <section className="flex flex-col gap-2 rounded-lg border border-l-4 border-cyan-200 border-l-cyan-500 bg-cyan-50/60 px-4 py-3">
        <span className="text-[11px] font-semibold tracking-wider text-cyan-700 uppercase">
          {t("predictedDeliveries.summary.howTitle")}
        </span>
        <ul className="flex list-disc flex-col gap-1.5 pl-4 text-sm text-gray-700">
          {how.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default ShipmentOverview;
